import React, { SyntheticEvent } from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import classNames from 'classnames';
import { MessageDescriptor, useIntl } from 'react-intl';

import { CurrentlyPlaying } from 'components/ConnectedAudioPlayer/hls-helper';
import FollowSpeaker from 'components/FollowSpeaker';
import IconFemtasy from 'components/IconFemtasy';
import LoveIcon from 'components/LoveIcon';
import NestedLink from 'components/NestedLink';
import PlayButton from 'components/PlayButton';
import Typography from 'components/Typography';

import { isProduction } from 'helpers/env';
import { metricsQueue } from 'helpers/metricsQueue';

import { useAuth } from 'hooks/useAuth';
import { usePlayer } from 'hooks/usePlayer';
import { PlayNextResult, useQueue } from 'hooks/useQueue';
import { useTracking } from 'hooks/useTracking';

import { useUrls } from 'constants/urls';
import { ContentType } from 'types/contentType';
import './style.scss';

const trackingName = (
  currentlyPlaying: CurrentlyPlaying | undefined,
  activeSubscription: boolean,
) => {
  if (!activeSubscription) {
    return 'mini_player_shopping_cart';
  } else if (currentlyPlaying?.type === 'speaker') {
    return 'mini_player_speaker';
  } else {
    return 'mini_player_standard';
  }
};

const trackingPiece = (currentlyPlaying: CurrentlyPlaying | undefined) => {
  return currentlyPlaying
    ? `${currentlyPlaying.type}_${currentlyPlaying.id}`
    : '';
};

const titleForCurrentlyPlaying = (
  current: CurrentlyPlaying,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, string>,
  ) => string,
): string | undefined => {
  const teaser = formatMessage({ id: 'components.MiniPlayer.title' });

  if (current.type === 'speaker') return teaser;
  if (current.type === 'other') return current.otherTitle;

  if (current.audioType === 'audio') {
    return current.storyTitle;
  } else {
    return `${teaser} - ${current.storyTitle}`;
  }
};

const ProgressBar = () => {
  const { mediaDuration, currentTime, fullAudioPlayed } = usePlayer();

  let progress = 0;

  if (mediaDuration) {
    progress = fullAudioPlayed
      ? 100
      : Math.round((currentTime / mediaDuration) * 100);
  }

  return (
    <div className="MiniPlayer__ProgressBar__wrapper">
      <div
        style={{
          width: `${progress}%`,
        }}
        className="MiniPlayer__ProgressBar"
      />
    </div>
  );
};

const Labels = () => {
  const { currentlyPlaying } = usePlayer();
  const { formatMessage } = useIntl();

  return (
    <div className="MiniPlayer__Labels">
      {currentlyPlaying && (
        <>
          <Typography variant="h4" className="MiniPlayer__Title">
            {titleForCurrentlyPlaying(currentlyPlaying, formatMessage)}
          </Typography>
          <Typography variant="small-thick" className="MiniPlayer__Subtitle">
            {currentlyPlaying.speakerName}
          </Typography>
        </>
      )}
    </div>
  );
};

const ShoppingCart = () => {
  const { hasPremiumAccess } = useAuth();
  const { currentlyPlaying } = usePlayer();
  const { trackContentInteraction } = useTracking();
  const { pricingPage } = useUrls();

  const handleClick = React.useCallback(
    (e: SyntheticEvent) => {
      trackContentInteraction(
        'click',
        'mini_player_shopping_cart_button',
        trackingPiece(currentlyPlaying),
      );
    },
    [currentlyPlaying, trackContentInteraction],
  );

  if (hasPremiumAccess) return null;

  return (
    <NestedLink
      to={pricingPage}
      onClick={handleClick}
      className="MiniPlayer__shoppingCart"
    >
      <IconFemtasy icon="Unlockedlockline" size="32" />
    </NestedLink>
  );
};

const StoryLove = () => {
  const { hasPremiumAccess } = useAuth();
  const { currentlyPlaying } = usePlayer();

  if (!hasPremiumAccess) return null;
  if (
    currentlyPlaying?.type !== 'story' ||
    currentlyPlaying?.contentType === ContentType.Series
  )
    return null;

  return (
    <div className="MiniPlayer__loveStory">
      <LoveIcon
        loveSvg={<IconFemtasy icon="Heartline" size="32" />}
        lovedSvg={<IconFemtasy icon="Heartsolid" size="32" />}
        itemId={currentlyPlaying.id}
        matomoName={trackingName(currentlyPlaying, hasPremiumAccess)}
        matomoPiece={trackingPiece(currentlyPlaying)}
      />
    </div>
  );
};

const InternalFollowSpeaker = () => {
  const { hasPremiumAccess } = useAuth();
  const { currentlyPlaying } = usePlayer();

  if (!hasPremiumAccess) return null;
  if (currentlyPlaying?.type !== 'speaker') return null;

  return (
    <FollowSpeaker
      speaker={{ id: currentlyPlaying.id }}
      matomoName={trackingName(currentlyPlaying, hasPremiumAccess)}
    />
  );
};

const OpenPlayer = () => {
  const { currentlyPlaying } = usePlayer();
  const { hasPremiumAccess } = useAuth();
  const { trackContentInteraction } = useTracking();

  const clickHandler = React.useCallback(() => {
    trackContentInteraction(
      'click',
      'mini_player_expander_arrow',
      trackingName(currentlyPlaying, hasPremiumAccess),
    );
  }, [hasPremiumAccess, currentlyPlaying, trackContentInteraction]);

  if (currentlyPlaying?.type !== 'story') return null;

  return (
    <div className="MiniPlayer__openPlayer" onClick={clickHandler}>
      <IconFemtasy icon="Arrowup" size="32" />
    </div>
  );
};

const CloseMiniPlayer = ({ onClick }: { onClick: () => void }) => {
  const { fullAudioPlayed, currentlyPlaying } = usePlayer();
  const { trackContentInteraction } = useTracking();

  const clickHandler = React.useCallback(() => {
    onClick();
    trackContentInteraction(
      'click',
      'mini_player_close_button',
      trackingPiece(currentlyPlaying),
    );
  }, [currentlyPlaying, onClick, trackContentInteraction]);

  if (!fullAudioPlayed) return null;

  return (
    <div className="MiniPlayer__close" onClick={clickHandler}>
      <IconFemtasy icon="Cancel" size="32" />
    </div>
  );
};

type IProps = {
  fixedToScreenBottom?: boolean;
  interactive?: boolean;
};

const MiniPlayer = ({ fixedToScreenBottom, interactive = true }: IProps) => {
  const {
    togglePlay,
    isPlaying,
    isLoading,
    currentlyPlaying,
    fullAudioPlayed,
    stopPlayback,
    showFullPlayer,
    isPlayingSeries,
  } = usePlayer();
  const { trackContentInteraction } = useTracking();
  const { hasPremiumAccess } = useAuth();
  const { playNextStory } = useQueue();

  React.useEffect(() => {
    if (fullAudioPlayed) {
      const result = playNextStory();

      metricsQueue.increment('player_autoplay_next_total', {
        outcome:
          result === PlayNextResult.NextStoryPlayed ? 'success' : 'empty_queue',
      });

      if (result === PlayNextResult.NextStoryPlayed) {
        trackContentInteraction(
          'next_audio_autoplay',
          'player',
          `story_${currentlyPlaying?.id}`,
        );
      }
    }
    // We dont want this useEffect to be called when playNext changes
    // as it creates circular dependency and breaks the flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentlyPlaying?.id, fullAudioPlayed, trackContentInteraction]);

  const onClick = React.useCallback(() => {
    if (currentlyPlaying?.type === 'story' && interactive) {
      showFullPlayer();
      trackContentInteraction(
        'click',
        trackingName(currentlyPlaying, hasPremiumAccess),
        trackingPiece(currentlyPlaying),
      );
      tracker.sendEvent(
        'Miniplayer',
        [
          {
            action: 'open_full_player',
            audioName: currentlyPlaying.storyTitle,
            pageUrl: window.location.href,
          },
        ],
        !isProduction(),
        [dataLayerProvider],
      );
    }
  }, [
    hasPremiumAccess,
    interactive,
    currentlyPlaying,
    showFullPlayer,
    trackContentInteraction,
  ]);

  const onPlayButtonClick = React.useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      trackContentInteraction(
        isPlaying ? 'pause' : 'continue',
        trackingName(currentlyPlaying, hasPremiumAccess),
        trackingPiece(currentlyPlaying),
        currentlyPlaying?.sourceUrl,
      );

      togglePlay();
    },
    [
      hasPremiumAccess,
      currentlyPlaying,
      isPlaying,
      togglePlay,
      trackContentInteraction,
    ],
  );

  if (!currentlyPlaying) return null;

  if (isPlayingSeries) {
    return null;
  }

  return (
    <div
      data-testid="miniplayer"
      className={classNames('MiniPlayer', { fixedToScreenBottom })}
      onClick={onClick}
    >
      <div className="MiniPlayer__content">
        <ProgressBar />
        <div className="MiniPlayer__PlayButton">
          <PlayButton
            size="40"
            isPlaying={isPlaying}
            isLoading={isLoading}
            alreadyListened={fullAudioPlayed}
            onClick={onPlayButtonClick}
          />
        </div>
        <Labels />
        <div className="MiniPlayer__Controls">
          <ShoppingCart />
          {interactive && (
            <>
              <StoryLove />
              <InternalFollowSpeaker />
              <OpenPlayer />
            </>
          )}
          <CloseMiniPlayer onClick={stopPlayback} />
        </div>
      </div>
    </div>
  );
};

export default MiniPlayer;
